import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Search, ReceiptText, BanknoteArrowUp, CircleCheckBig } from 'lucide-react';
import { closeOpenTicket, fetchOpenTickets } from '../../services/ticketService';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #7f1b1c 0%, #640003 100%);
  padding: 120px 20px 80px;
`;

const Card = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #1e293b;
`;

const Subtitle = styled.p`
  margin: 0 0 20px;
  color: #64748b;
`;

const Filters = styled.form`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  align-items: end;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.875rem;
  color: #334155;
  margin-bottom: 6px;
  font-weight: 600;
`;

const Input = styled.input`
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.95rem;
  outline: none;

  &:focus {
    border-color: #c31321;
    box-shadow: 0 0 0 3px rgba(195, 19, 33, 0.1);
  }
`;

const SearchButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 11px 14px;
  background: #c31321;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const ErrorText = styled.p`
  margin: 8px 0;
  color: #b91c1c;
  font-weight: 600;
`;

const SuccessText = styled.p`
  margin: 8px 0;
  color: #15803d;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TH = styled.th`
  text-align: left;
  background: #f8fafc;
  color: #1e293b;
  padding: 10px;
  font-size: 0.85rem;
`;

const TD = styled.td`
  padding: 10px;
  border-top: 1px solid #e2e8f0;
  font-size: 0.9rem;
  color: #334155;
`;

const Badge = styled.span`
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #dcfce7;
  color: #166534;
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  background: ${(props) => (props.variant === 'pay' ? '#1d4ed8' : '#15803d')};
`;

const EmptyState = styled.div`
  padding: 28px 12px;
  text-align: center;
  color: #64748b;
`;

const formatDateTime = (isoString) => {
  if (!isoString) {
    return '-';
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return isoString;
  }
  return date.toLocaleString();
};

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const OpenTickets = () => {
  const navigate = useNavigate();
  const [key, setKey] = useState('');
  const [dateFrom, setDateFrom] = useState(toDateInputValue(new Date(Date.now() - (30 * 24 * 60 * 60 * 1000))));
  const [dateTo, setDateTo] = useState(toDateInputValue(new Date()));
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closingId, setClosingId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const hasData = useMemo(() => tickets.length > 0, [tickets]);

  const handleSearch = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!key.trim()) {
      setError('Please enter a plate key.');
      return;
    }

    setLoading(true);
    try {
      const payload = await fetchOpenTickets({
        key: key.trim(),
        eventDateAfter: `${dateFrom}T00:00:00`,
        eventDateBefore: `${dateTo}T23:59:59`
      });
      setTickets(Array.isArray(payload.data) ? payload.data : []);
    } catch (searchError) {
      setError(searchError.message || 'Could not load tickets.');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async (ticket) => {
    const eventId = ticket?.attributes?.id;
    if (!eventId) {
      setError('Missing event id for this ticket.');
      return;
    }

    setError('');
    setSuccess('');
    setClosingId(eventId);
    try {
      await closeOpenTicket(eventId, {
        orderNumber: ticket?.attributes?.orderNumber,
        pan: ticket?.attributes?.pan,
        amount: ticket?.attributes?.amount
      });
      setTickets((prev) => prev.filter((item) => item?.attributes?.id !== eventId));
      setSuccess(`Ticket ${eventId} close request submitted.`);
    } catch (closeError) {
      setError(closeError.message || 'Ticket close failed.');
    } finally {
      setClosingId('');
    }
  };

  const handlePay = (ticket) => {
    const amount = ticket?.attributes?.amount || '';
    navigate('/kp-card/recharge', {
      state: {
        prefill: {
          amount: amount
        }
      }
    });
  };

  return (
    <PageContainer>
      <Card>
        <Title>Open Parking Tickets</Title>
        <Subtitle>
          Search OPEN O&apos;city parking events by plate key and date range, then proceed to pay and close.
        </Subtitle>

        <Filters onSubmit={handleSearch}>
          <Field>
            <Label>Plate Key</Label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Example: 2583"
            />
          </Field>
          <Field>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </Field>
          <SearchButton type="submit" disabled={loading}>
            <Search size={16} />
            {loading ? 'Loading...' : 'Fetch Open Tickets'}
          </SearchButton>
        </Filters>

        {error && <ErrorText>{error}</ErrorText>}
        {success && <SuccessText>{success}</SuccessText>}

        <TableWrap>
          <Table>
            <thead>
              <tr>
                <TH>Plate/PAN</TH>
                <TH>Event ID</TH>
                <TH>Parking Slot</TH>
                <TH>Amount</TH>
                <TH>Event Date</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {hasData && tickets.map((item) => {
                const attributes = item.attributes || {};
                const eventId = attributes.id || '';
                const amountCurrency = attributes.amountCurrency || 'NAD';
                return (
                  <tr key={eventId}>
                    <TD>{attributes.maskedPan || attributes.pan || '-'}</TD>
                    <TD>{eventId}</TD>
                    <TD>{attributes?.params?.vehicles_parking_place_number || '-'}</TD>
                    <TD>{amountCurrency} {attributes.amount || '-'}</TD>
                    <TD>{formatDateTime(attributes.eventDate)}</TD>
                    <TD><Badge>{attributes.status || 'OPEN'}</Badge></TD>
                    <TD>
                      <RowActions>
                        <ActionButton type="button" variant="pay" onClick={() => handlePay(item)}>
                          <BanknoteArrowUp size={14} /> Pay
                        </ActionButton>
                        <ActionButton
                          type="button"
                          onClick={() => handleClose(item)}
                          disabled={closingId === eventId}
                        >
                          <CircleCheckBig size={14} />
                          {closingId === eventId ? 'Closing...' : 'Close'}
                        </ActionButton>
                      </RowActions>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {!hasData && !loading && (
            <EmptyState>
              <ReceiptText size={18} style={{ marginBottom: 8 }} />
              No open tickets found for this filter.
            </EmptyState>
          )}
        </TableWrap>
      </Card>
    </PageContainer>
  );
};

export default OpenTickets;
